import React from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { adminApi } from '../../../lib/adminAuth';
import ClientPicker from './ClientPicker';
import { errMsg } from './docUtils';

export default function NewProposalDialog({ open, onClose, onCreated }) {
  const [type, setType] = React.useState('prospect');
  const [clientId, setClientId] = React.useState('');
  const EMPTY = { recipient_name: '', recipient_company: '', brand_name: '', recipient_email: '', recipient_phone: '', recipient_gstin: '', recipient_state: '', recipient_address: '' };
  const [f, setF] = React.useState(EMPTY);
  const [busy, setBusy] = React.useState(false);
  const valid = type === 'client' ? !!clientId : !!(f.recipient_company.trim() || f.recipient_name.trim());
  const create = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post('/api/admin/proposals', type === 'client' ? { recipient_type: 'client', client_id: clientId } : { recipient_type: 'prospect', ...f });
      toast.success(`${r.data.proposal_number} created`);
      onCreated(r.data._id); onClose();
      setF(EMPTY); setClientId('');
    } catch (e) { toast.error(errMsg(e, 'Could not create the proposal')); }
    finally { setBusy(false); }
  };
  const inp = (k, label, extra = {}) => <Input placeholder={label} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`new-proposal-${k}`} {...extra} />;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-proposal-dialog">
        <DialogHeader><DialogTitle>New proposal</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100">
          {[['prospect', 'New prospect'], ['client', 'Existing client']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setType(k)} data-testid={`new-proposal-type-${k}`}
              className={`py-1.5 rounded-lg text-sm font-semibold transition-colors ${type === k ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>{l}</button>
          ))}
        </div>
        {type === 'client' ? <ClientPicker value={clientId} onChange={setClientId} testId="new-proposal-client" /> : (
          <div className="space-y-2">
            {inp('recipient_company', 'Company name')}
            {inp('brand_name', 'Brand (if different)')}
            {inp('recipient_name', 'Contact person')}
            {inp('recipient_email', 'Contact email', { type: 'email' })}
            <div className="grid grid-cols-2 gap-2">{inp('recipient_phone', 'Phone')}{inp('recipient_gstin', 'GSTIN', { maxLength: 15 })}</div>
            {inp('recipient_state', 'State')}
            <textarea value={f.recipient_address} onChange={(e) => setF({ ...f, recipient_address: e.target.value })} rows={2} placeholder="Registered office address"
              className="w-full px-3 py-2 rounded-md border border-input text-sm" data-testid="new-proposal-recipient_address" />
          </div>
        )}
        <p className="text-xs text-slate-500">Scope, intro and key terms are pre-filled from the Scope Library.</p>
        <DialogFooter>
          <Button onClick={create} disabled={!valid || busy} className="bg-gray-900 hover:bg-gray-800" data-testid="new-proposal-create">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Create proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
